if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.setupGalleryNavigation();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      setupGalleryNavigation() {
        const items = this.elements.viewer?.querySelectorAll('.product__media-item[data-media-id]');
        if (!items || items.length < 2) return;
        this.hbNavigation = document.createElement('div');
        this.hbNavigation.className = 'hb-gallery-navigation';
        this.hbNavigation.setAttribute('role', 'group');
        this.hbNavigation.setAttribute('aria-label', this.getAttribute('aria-label'));
        const previous = document.createElement('button');
        previous.type = 'button';
        previous.className = 'hb-gallery-step';
        previous.textContent = '‹';
        previous.setAttribute('aria-label', this.elements.viewer.querySelector('.slider-button--prev').getAttribute('aria-label'));
        previous.addEventListener('click', () => this.moveGalleryMedia(-1));
        const next = document.createElement('button');
        next.type = 'button';
        next.className = 'hb-gallery-step';
        next.textContent = '›';
        next.setAttribute('aria-label', this.elements.viewer.querySelector('.slider-button--next').getAttribute('aria-label'));
        next.addEventListener('click', () => this.moveGalleryMedia(1));
        this.hbCounter = document.createElement('span');
        this.hbCounter.className = 'hb-gallery-count';
        this.hbCounter.setAttribute('aria-live', 'polite');
        this.hbNavigation.append(previous, this.hbCounter, next);
        this.elements.viewer.after(this.hbNavigation);
        if (!document.getElementById('hb-gallery-navigation-style')) {
          const style = document.createElement('style');
          style.id = 'hb-gallery-navigation-style';
          style.textContent = '.hb-gallery-navigation{display:flex;align-items:center;justify-content:center;gap:14px;margin:14px 0 8px}.hb-gallery-navigation[hidden]{display:none}.hb-gallery-step{display:flex;align-items:center;justify-content:center;width:44px;height:44px;padding:0;border:1px solid #d5cbbf;border-radius:50%;background:#fff;color:#26352e;font:28px/1 Arial,sans-serif;cursor:pointer}.hb-gallery-step:hover{background:#EEEAE5}.hb-gallery-step:focus-visible{outline:2px solid currentColor;outline-offset:3px}.hb-gallery-count{min-width:48px;text-align:center;font-size:13px;font-variant-numeric:tabular-nums}@media(max-width:749px){html:has(media-gallery){overflow-x:clip}}';
          document.head.append(style);
        }
        this.updateGalleryNavigation();
      }

      moveGalleryMedia(step) {
        const items = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
        const currentId = this.elements.thumbnails?.querySelector('[aria-current="true"]')?.closest('[data-target]')?.dataset.target
          || this.elements.viewer.querySelector('.is-active[data-media-id]')?.dataset.mediaId;
        const currentIndex = Math.max(0, items.findIndex(item => item.dataset.mediaId === currentId));
        const nextItem = items[(currentIndex + step + items.length) % items.length];
        if (nextItem) this.setActiveMedia(nextItem.dataset.mediaId, false);
      }

      updateGalleryNavigation(mediaId) {
        if (!this.hbNavigation) return;
        const items = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
        const currentId = mediaId || this.elements.thumbnails?.querySelector('[aria-current="true"]')?.closest('[data-target]')?.dataset.target
          || this.elements.viewer.querySelector('.is-active[data-media-id]')?.dataset.mediaId;
        const currentIndex = Math.max(0, items.findIndex(item => item.dataset.mediaId === currentId));
        this.hbCounter.textContent = (currentIndex + 1) + ' / ' + items.length;
        this.hbNavigation.hidden = items.length < 2;
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        this.updateGalleryNavigation(thumbnail.dataset.target);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
